import { Alert, Button, Form, Input, Select, Typography } from 'antd';
import React, { useState } from 'react';
import i18n from '../../i18n';
import { State } from '~/types';
import api from './api';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useUserSuggestion } from './hooks';

type VoidPanelState =
  | {
      type: State.Success;
    }
  | {
      type: State.Loading;
    }
  | {
      type: State.Error;
      message: string | null;
    };

const AuthorizePanel = ({
  entityId,
  permissionName,
  onSubmit,
}: {
  entityId: string;
  permissionName: string;
  onSubmit: ({
    userId,
    actionToken,
  }: {
    userId: string;
    actionToken: string;
  }) => void;
}) => {
  const { data: users } = useSuspenseQuery({
    queryKey: ['fetchAuthorizedUser'],
    queryFn: async () => {
      const { userRestaurantRoles } = await api.fetchUserRestaurantRoles();
      return userRestaurantRoles.map(({ user }) => user);
    },
  });

  const { userSuggestions, saveUserAsSuggestion } = useUserSuggestion(users);

  const [form] = Form.useForm();

  const [state, setState] = useState<VoidPanelState>({ type: State.Success });

  const handleSubmit = async (values: any) => {
    try {
      setState({ type: State.Loading });

      const user = users.find(({ userId }) => userId === values.userId);
      if (!user) throw new Error();

      const { isAuthorized, actionToken } = await api.confirmAuthorize({
        entityId,
        username: user.username,
        pin: values.pin,
        permissionName,
      });

      if (!isAuthorized) throw new Error();

      setState({ type: State.Success });

      saveUserAsSuggestion(user.userId);
      onSubmit({ userId: user.userId, actionToken });
    } catch (_) {
      setState({ type: State.Error, message: 'Gagal otorisasi' });
    }
  };

  const onClickUserSuggestion = (id: string) => {
    form.setFieldValue('userId', id);
  };

  return (
    <div>
      <Typography.Title level={4}>Otorisasi</Typography.Title>
      {state.type === State.Error ? (
        <Alert
          message={state.message}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      ) : null}
      <Form form={form} onFinish={handleSubmit} layout="vertical">
        <Form.Item
          name="userId"
          label={i18n.t('void_panel_user')}
          rules={[
            { required: true, message: i18n.t('void_panel_form_user_error') },
          ]}
        >
          <Select placeholder={i18n.t('void_panel_user_placeholder')}>
            {users.map(({ userId, username }) => (
              <Select.Option value={userId} key={userId}>
                {username}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {userSuggestions.length ? (
          <div className="flex items-center mb-6">
            <p className="mb-0 mr-2">Terakhir:</p>
            <div className="flex items-center gap-2">
              {userSuggestions.map(({ userId, username }) => {
                return (
                  <div
                    key={userId}
                    onClick={() => onClickUserSuggestion(userId)}
                    className="cursor-pointer bg-neutral-200 rounded-full px-4 py-1"
                  >
                    {username}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        <Form.Item
          name="pin"
          label={i18n.t('void_panel_pin')}
          rules={[
            { required: true, message: i18n.t('void_panel_form_pin_error') },
          ]}
        >
          <Input.Password style={{ width: '100%' }} />
        </Form.Item>
        <div className="mt-9">
          <Button
            type="primary"
            htmlType="submit"
            loading={state.type === State.Loading}
            size="large"
            block
          >
            Submit
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AuthorizePanel;
